import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchHealthDigestRuns,
  previewHealthDigest,
  sendHealthDigestNow,
} from '../../api/healthDigest'
import { useActionToast } from '../../lib/useActionToast'
import { localDateKey, localParts, LOCAL_TZ } from '../../lib/localDate'

function pacificYesterdayIso(): string {
  const p = localParts(Date.now(), LOCAL_TZ)
  const ref = new Date(Date.UTC(p.year, p.month - 1, p.day, 12))
  ref.setUTCDate(ref.getUTCDate() - 1)
  return localDateKey(ref, LOCAL_TZ)
}

export default function AdminHealthDigestScreen() {
  const [date, setDate] = useState(() => pacificYesterdayIso())
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const { Toast, showToast } = useActionToast()

  const runs = useQuery({
    queryKey: ['health-digest-runs'],
    queryFn: fetchHealthDigestRuns,
    staleTime: 30_000,
  })

  const previewMut = useMutation({
    mutationFn: () => previewHealthDigest(date),
    onSuccess: (html) => {
      setPreviewHtml(html)
      showToast('Preview loaded')
    },
    onError: (e: Error) => showToast(e.message),
  })

  const sendMut = useMutation({
    mutationFn: () => sendHealthDigestNow(date, false),
    onSuccess: () => {
      showToast('Send triggered (log-only on staging)')
      runs.refetch()
    },
    onError: (e: Error) => showToast(e.message),
  })

  return (
    <div className="admin-page" data-testid="admin-health-digest-page">
      <h1 className="admin-page-title">Customer health digest</h1>
      <p className="admin-page-sub">Internal operator email · metadata only</p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'flex-end',
          marginTop: 20,
          marginBottom: 20,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Digest date (Pacific day)
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="health-digest-date"
            className="input"
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)' }}
          />
        </label>
        <button
          type="button"
          className="btn"
          onClick={() => setDate(pacificYesterdayIso())}
        >
          Yesterday
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={previewMut.isPending}
          onClick={() => previewMut.mutate()}
          data-testid="health-digest-preview"
        >
          Load preview
        </button>
        <button
          type="button"
          className="btn"
          disabled={sendMut.isPending}
          onClick={() => sendMut.mutate()}
          data-testid="health-digest-send"
        >
          Send now
        </button>
      </div>

      {previewHtml && (
        <div
          data-testid="health-digest-preview-frame"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 12,
            overflow: 'hidden',
            maxWidth: 640,
            background: '#f3f4f6',
          }}
        >
          <iframe
            title="Health digest preview"
            srcDoc={previewHtml}
            style={{ width: '100%', minHeight: 480, border: 0 }}
          />
        </div>
      )}

      <h2 style={{ fontSize: 16, marginTop: 32 }}>Recent runs</h2>
      {runs.isLoading ? (
        <p>Loading…</p>
      ) : (
        <table className="admin-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Subject</th>
              <th>Sent</th>
              <th>Log only</th>
            </tr>
          </thead>
          <tbody>
            {(runs.data ?? []).map((row) => (
              <tr key={row.digest_date}>
                <td>{row.digest_date}</td>
                <td>{row.subject ?? '—'}</td>
                <td>{row.sent_at ? new Date(row.sent_at).toLocaleString() : '—'}</td>
                <td>{row.log_only ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {Toast}
    </div>
  )
}
